import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Switch,
  message,
  Select,
  DatePicker,
  Row,
  Col,
  Statistic,
  Typography,
  Segmented,
  Grid,
} from 'antd';
import {
  SyncOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  DollarOutlined,
  EyeOutlined,
  AimOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import adsService, {
  AdAccount,
  AdInsight,
  InsightsSummary,
  FilterParams,
} from '../../services/ads.service';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

type EntityType = 'campaigns' | 'adsets' | 'ads';

const AdList: React.FC = () => {
  const [accounts, setAccounts] = useState<AdAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
  const [entityType, setEntityType] = useState<EntityType>('campaigns');
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState<{ data: AdInsight[]; summary: InsightsSummary } | null>(null);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [quickFilter, setQuickFilter] = useState<string>('today');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // Fetch Ad Accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const data = await adsService.getAdAccounts();
        setAccounts(data);
        
        // Try to get from localStorage
        const savedAccount = localStorage.getItem('selected_ad_account');
        if (savedAccount && data.find(a => a._id === savedAccount)) {
          setSelectedAccount(savedAccount);
        } else if (data.length > 0) {
          setSelectedAccount(data[0]._id);
        }
      } catch (error) {
        message.error('Không thể tải danh sách tài khoản');
      }
    };
    fetchAccounts();
  }, []);

  const handleAccountChange = (value: string) => {
    setSelectedAccount(value);
    localStorage.setItem('selected_ad_account', value);
  };

  // Fetch Data based on entity type
  const fetchData = useCallback(async () => {
    if (!selectedAccount) return;

    setLoading(true);
    try {
      const filter: FilterParams = {
        adAccountId: selectedAccount,
        page: pagination.current,
        limit: pagination.pageSize,
        period: quickFilter,
      };

      if (dateRange) {
        filter.startDate = dateRange[0].format('YYYY-MM-DD');
        filter.endDate = dateRange[1].format('YYYY-MM-DD');
        filter.period = 'custom';
      }

      let result;
      switch (entityType) {
        case 'campaigns':
          result = await adsService.getCampaigns(filter);
          break;
        case 'adsets':
          result = await adsService.getAdSets(filter);
          break;
        case 'ads':
          result = await adsService.getAds(filter);
          break;
      }

      // Backend already sorts by status, use data directly
      setData(result.data);
      setTotal(result.total);

      // Also fetch insights
      const insightsResult = await adsService.getInsights(filter);
      setInsights(insightsResult);
    } catch (error) {
      message.error('Không thể tải dữ liệu');
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, entityType, pagination, quickFilter, dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Toggle Status
  const handleToggle = async (record: any, checked: boolean) => {
    setTogglingId(record._id);
    const level = entityType === 'campaigns' ? 'campaign' : entityType === 'adsets' ? 'adset' : 'ad';
    const action = checked ? 'ACTIVE' : 'PAUSED';

    try {
      const result = await adsService.toggleStatus(record._id, level, action);
      if (result.success) {
        message.success(`Đã ${checked ? 'bật' : 'tắt'} quảng cáo`);
        fetchData();
      } else {
        message.error('Cập nhật trạng thái thất bại');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Lỗi cập nhật trạng thái');
    } finally {
      setTogglingId(null);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('vi-VN').format(value);
  };

  const getStatusTag = (status: string) => {
    const statusMap: Record<string, { color: string; icon: React.ReactNode; text: string }> = {
      ACTIVE: { color: 'success', icon: <PlayCircleOutlined />, text: 'Đang chạy' },
      PAUSED: { color: 'warning', icon: <PauseCircleOutlined />, text: 'Tạm dừng' },
      DELETED: { color: 'error', icon: null, text: 'Đã xóa' },
      ARCHIVED: { color: 'default', icon: null, text: 'Lưu trữ' },
      PENDING_REVIEW: { color: 'processing', icon: null, text: 'Chờ duyệt' },
      DISAPPROVED: { color: 'error', icon: null, text: 'Bị từ chối' },
    };
    const config = statusMap[status] || { color: 'default', icon: null, text: status };
    return (
      <Tag color={config.color} icon={config.icon} style={{ margin: 0, fontSize: isMobile ? 10 : 12 }}>
        {config.text}
      </Tag>
    );
  };

  // Get parent name for adsets and ads
  const getParentName = (record: any): string => {
    if (entityType === 'adsets') {
      return record.campaignId?.name || '';
    }
    if (entityType === 'ads') {
      return record.adSetId?.name || '';
    }
    return '';
  };

  const baseColumns: ColumnsType<any> = [
    {
      title: 'Tên',
      dataIndex: 'name',
      key: 'name',
      fixed: (isMobile ? undefined : 'left') as any,
      width: isMobile ? 220 : 280,
      render: (text, record) => (
        <div>
          <Text strong style={{ display: 'block', maxWidth: isMobile ? 200 : 260, fontSize: isMobile ? 12 : 14 }} ellipsis={{ tooltip: text }}>
            {text}
          </Text>
          {getParentName(record) && (
            <Text type="secondary" style={{ fontSize: 10 }}>
              {entityType === 'adsets' ? 'C.Dịch' : 'Nhóm'}: {getParentName(record)}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      align: 'center',
      render: (status, record) => (!record.isSummary ? getStatusTag(status) : null),
    },
    {
      title: 'H.Động',
      key: 'toggle',
      width: 80,
      align: 'center',
      render: (_, record) => (
        !record.isSummary && <Switch
          size="small"
          checked={record.status === 'ACTIVE'}
          loading={togglingId === record._id}
          onChange={(checked) => handleToggle(record, checked)}
          disabled={record.status === 'DELETED' || record.status === 'ARCHIVED'}
        />
      ),
    },
  ];

  // Insight columns for all levels
  const insightColumns: ColumnsType<any> = [
    {
      title: 'Kết quả',
      dataIndex: 'results',
      key: 'results',
      width: 90,
      align: 'right',
      render: (value) => <span style={{ fontSize: isMobile ? 12 : 14 }}>{formatNumber(value || 0)}</span>,
    },
    {
      title: 'Chi phí/KQ',
      dataIndex: 'costPerResult',
      key: 'costPerResult',
      width: 120,
      align: 'right',
      render: (value) => <span style={{ fontSize: isMobile ? 12 : 14 }}>{value > 0 ? formatCurrency(value) : '-'}</span>,
    },
    {
      title: 'Đã chi tiêu',
      dataIndex: 'spend',
      key: 'spend',
      width: 120,
      align: 'right',
      render: (value) => <span style={{ fontSize: isMobile ? 12 : 14 }}>{formatCurrency(value || 0)}</span>,
    },
  ];

  // Objective column for campaigns
  const objectiveColumn: ColumnsType<any> =
    entityType === 'campaigns'
      ? [
          {
            title: 'Mục tiêu',
            dataIndex: 'objective',
            key: 'objective',
            width: 130,
            render: (objective) => {
              const objectiveMap: Record<string, string> = {
                OUTCOME_TRAFFIC: 'Lưu lượng',
                OUTCOME_ENGAGEMENT: 'Tương tác',
                OUTCOME_LEADS: 'Khách hàng',
                OUTCOME_SALES: 'Bán hàng',
                OUTCOME_AWARENESS: 'Nhận diện',
                REACH: 'Tiếp cận',
                TRAFFIC: 'Lưu lượng',
                CONVERSIONS: 'Chuyển đổi',
                MESSAGES: 'Tin nhắn',
              };
              return <Tag style={{ fontSize: 10, margin: 0 }}>{objectiveMap[objective] || objective}</Tag>;
            },
          },
        ]
      : [];

  const columns: ColumnsType<any> = [...baseColumns, ...objectiveColumn, ...insightColumns];

  return (
    <div style={{ padding: isMobile ? '12px' : '24px' }}>
      <Card
        title={
          <Space size={isMobile ? 8 : 12}>
            <AimOutlined style={{ fontSize: isMobile ? 20 : 24, color: '#1890ff' }} />
            <Title level={isMobile ? 5 : 4} style={{ margin: 0 }}>
              {isMobile ? 'Quản lý QC' : 'Quản lý quảng cáo'}
            </Title>
          </Space>
        }
        extra={
          <Space size={isMobile ? 4 : 8}>
            <Select
              style={{ width: isMobile ? 120 : 250 }}
              placeholder="Chọn tài khoản"
              value={selectedAccount}
              size={isMobile ? 'small' : 'middle'}
              onChange={handleAccountChange}
              options={accounts.map((a) => ({ label: a.name, value: a._id }))}
            />
            <Button 
              icon={<SyncOutlined />} 
              size={isMobile ? 'small' : 'middle'} 
              onClick={fetchData}
            >
              {isMobile ? '' : 'Làm mới'}
            </Button>
          </Space>
        }
        bodyStyle={{ padding: isMobile ? '12px' : '24px' }}
      >
        {/* Filters */}
        <div style={{ marginBottom: 24, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <Segmented
              size={isMobile ? 'small' : 'middle'}
              block={isMobile}
              value={entityType}
              onChange={(value) => setEntityType(value as EntityType)}
              options={[
                { label: 'C.Dịch', value: 'campaigns' },
                { label: 'Nhóm QC', value: 'adsets' },
                { label: 'QC', value: 'ads' },
              ]}
            />
          </div>
          <div>
            <Segmented
              size={isMobile ? 'small' : 'middle'}
              block={isMobile}
              value={quickFilter}
              onChange={(value) => {
                setQuickFilter(value as string);
                setDateRange(null);
              }}
              options={[
                { label: 'H.Nay', value: 'today' },
                { label: 'H.Qua', value: 'yesterday' },
                { label: '7N', value: 'last_7_days' },
                { label: '30N', value: 'last_30_days' },
                { label: 'Tháng này', value: 'this_month' },
              ].filter(opt => !isMobile || opt.value !== 'this_week')}
            />
          </div>
          <div style={{ width: isMobile ? '100%' : 'auto' }}>
            <RangePicker
              size={isMobile ? 'small' : 'middle'}
              style={{ width: '100%' }}
              value={dateRange}
              onChange={(dates) => {
                if (dates) {
                  setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs]);
                  setQuickFilter('custom');
                }
              }}
              format="DD/MM/YYYY"
            />
          </div>
        </div>

        {/* Data Table */}
        <Table
          columns={columns}
          dataSource={
            insights && insights.summary
              ? [
                  {
                    _id: 'summary',
                    name: 'Tổng cộng',
                    status: 'SUMMARY',
                    results: insights.summary.totalResults,
                    spend: insights.summary.totalSpend,
                    costPerResult: insights.summary.avgCostPerResult,
                    isSummary: true,
                  },
                  ...data,
                ]
              : data
          }
          rowKey="_id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total,
            size: isMobile ? 'small' : undefined as any,
            showSizeChanger: !isMobile,
            showTotal: (total) => `Tổng ${total}`,
            onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
          }}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'Không có dữ liệu. Vui lòng đồng bộ tài khoản trước.' }}
        />
      </Card>
    </div>
  );
};

export default AdList;
